import { Download, Coffee, Mic, HardDrive, ClipboardCopy, ArrowRight, Heart } from "lucide-react";
import { TIMSCRIBE, TsHeader, TsFooter, useTsMeta } from "./shared";

const FEATURES = [
    { icon: Mic, title: "Speak naturally", body: "Pick a microphone once, set it as default, then record. TimScribe transcribes the note on this PC — GPU if you have one, CPU if not." },
    { icon: HardDrive, title: "Keep it local", body: "Dictate offline with the speech model included in your download. Nothing is uploaded." },
    { icon: ClipboardCopy, title: "Review and copy", body: "Edit the transcript, then copy it into the software you already use." },
];

export default function TimScribePage() {
    useTsMeta("TimScribe | Free offline dictation for Windows", "/timscribe");
    return (
        <div className="min-h-screen bg-[#f2f4f6] text-slate-700 antialiased" data-testid="timscribe-page">
            <TsHeader />
            <main>
                <section className="tb-grid border-b border-subtle">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 sol-reveal">
                        <span className="font-mono text-xs tracking-widest uppercase text-cyan-600">// Your voice. Your computer.</span>
                        <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
                            Less typing.
                            <br />
                            More time for people.
                        </h1>
                        <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
                            Turn spoken notes into editable text with TimScribe. Speech recognition happens on your Windows
                            computer, keeping your recordings and transcripts local.
                        </p>
                        <div className="mt-9 flex flex-col sm:flex-row gap-3">
                            <a
                                href={TIMSCRIBE.download}
                                download
                                data-testid="ts-download-trial"
                                className="inline-flex items-center justify-center gap-2 rounded bg-cyan-500 text-slate-900 font-mono text-sm tracking-wider font-bold px-6 py-3.5 hover:bg-cyan-400 transition-colors"
                            >
                                <Download className="h-4 w-4" /> DOWNLOAD FREE · WINDOWS
                            </a>
                            <a
                                href={TIMSCRIBE.stripe}
                                data-testid="ts-donate-hero"
                                className="inline-flex items-center justify-center gap-2 rounded border border-slate-300 bg-white text-slate-700 font-mono text-sm tracking-wider px-6 py-3.5 hover:border-cyan-500 hover:text-cyan-700 transition-colors"
                            >
                                <Coffee className="h-4 w-4" /> DONATE {TIMSCRIBE.donation} IF YOU LIKE IT
                            </a>
                        </div>
                        <p className="mt-5 font-mono text-[11px] tracking-wider text-slate-500 uppercase">
                            Windows 64-bit · Free · No account, no licence key, no subscription
                        </p>
                    </div>
                </section>

                <section className="py-16 sm:py-20 border-b border-subtle" data-testid="ts-screenshot">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                        <figure className="lg:col-span-8 rounded-xl border border-subtle bg-white p-3 shadow-sm">
                            <img
                                src="/timscribe/timscribe-app.png"
                                alt="TimScribe window: START RECORDING and STOP RECORDING buttons, a READY status showing GPU preferred (Vulkan) with CPU fallback, a microphone selector with Set as default and Refresh, the transcript area, and COPY / CLEAR & NEW NOTE buttons"
                                width="972"
                                height="631"
                                loading="lazy"
                                className="rounded-lg w-full h-auto"
                                data-testid="ts-screenshot-image"
                            />
                            <figcaption className="mt-3 font-mono text-[10px] tracking-widest text-slate-500 uppercase px-1">
                                FIG. 01 — TimScribe on Windows 11 · the whole app
                            </figcaption>
                        </figure>
                        <ul className="lg:col-span-4 space-y-6">
                            {[
                                ["One screen, no menus", "Record, review, copy. The status line tells you whether it's using your GPU (Vulkan) or the CPU fallback."],
                                ["Remembers your mic", "Pick a microphone, hit Set as default, and it's selected automatically next time. Refresh picks up newly plugged-in devices."],
                                ["You stay in control", "The transcript sits in an editable box. Nothing is saved or sent anywhere until you choose COPY."],
                            ].map(([t, b]) => (
                                <li key={t}>
                                    <div className="font-mono text-[10px] tracking-widest uppercase text-cyan-600">// {t}</div>
                                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">{b}</p>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                <section className="py-16 sm:py-20 border-b border-subtle bg-white" data-testid="ts-features">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {FEATURES.map(({ icon: Icon, title, body }) => (
                            <article key={title} className="rounded-lg border border-subtle bg-[#f2f4f6] p-7">
                                <Icon className="h-6 w-6 text-cyan-600" />
                                <h2 className="mt-5 font-display text-lg font-semibold text-slate-900">{title}</h2>
                                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section id="donate" className="py-20 sm:py-24 border-b border-subtle" data-testid="ts-donate">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <span className="font-mono text-xs tracking-widest uppercase text-cyan-600">// Price</span>
                            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Free. Really.</h2>
                            <p className="mt-5 text-slate-600 leading-relaxed max-w-xl">
                                TimScribe is free to download and use — no trial clock, no licence key, no account. I built it
                                because I wanted it, and I&rsquo;d rather people have it than not.
                            </p>
                            <p className="mt-3 text-sm text-slate-500">
                                If it saves you some typing and you feel like saying thanks, a $5 donation covers the hosting and
                                buys a coffee for the next build. Entirely optional.
                            </p>
                        </div>
                        <div className="lg:col-span-5 rounded-lg border border-subtle bg-white p-8 shadow-sm" data-testid="ts-donate-card">
                            <div className="flex items-baseline gap-3">
                                <span className="font-display text-5xl font-bold text-slate-900 tracking-tight">$0</span>
                                <span className="font-mono text-xs tracking-wider text-slate-500 uppercase">to use</span>
                            </div>
                            <div className="mt-1 font-mono text-[11px] tracking-wider text-slate-500 uppercase">{TIMSCRIBE.donation} donation · {TIMSCRIBE.donationNote}</div>
                            <a
                                href={TIMSCRIBE.stripe}
                                data-testid="ts-donate-checkout"
                                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded bg-cyan-500 text-slate-900 font-mono text-sm tracking-wider font-bold px-6 py-3.5 hover:bg-cyan-400 transition-colors"
                            >
                                <Heart className="h-4 w-4" /> DONATE {TIMSCRIBE.donation} <ArrowRight className="h-4 w-4" />
                            </a>
                            <p className="mt-4 text-xs text-slate-500" data-testid="ts-donate-note">
                                Secure one-time payment via Stripe. No subscription, and the software works exactly the same whether you donate or not.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="py-16 sm:py-20 bg-white" data-testid="ts-start">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                        <span className="font-mono text-xs tracking-widest uppercase text-cyan-600">// Ready to start?</span>
                        <p className="mt-3 text-slate-600 leading-relaxed max-w-2xl">
                            Download, extract the whole ZIP, and open <code className="font-mono text-[13px] px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-800 border border-cyan-100">TimScribe.exe</code>.
                            No Python installation or separate speech-model download.
                        </p>
                        <a href="/timscribe/system-requirements" data-testid="ts-requirements-link" className="mt-4 inline-flex items-center gap-2 font-mono text-xs tracking-wider font-bold text-cyan-700 hover:text-cyan-900 transition-colors">
                            SYSTEM REQUIREMENTS & GETTING-STARTED GUIDE <ArrowRight className="h-4 w-4" />
                        </a>
                        <p className="mt-6 font-mono text-[11px] tracking-wider text-slate-500">
                            Always review transcribed text before use. TimScribe is a dictation aid.
                        </p>
                    </div>
                </section>
            </main>
            <TsFooter />
        </div>
    );
}
